import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { CultivarActions } from '@galo-monitor/stores/cultivar'
import DataTable from '@galo-monitor/web-components/DataTable'
import Screen from '@galo-monitor/web-components/Screen'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import CultivarModal from '@galo-monitor/web-containers/modals/root/CultivarModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const Cultivars = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const { data: cultivars, mutate, isValidating } = useGaloMonitorApi('/admin/cultivars')

  const userActions = useMemo(
    () => getActionsBySlug(routes.cultivars.slug),
    [getActionsBySlug, routes.cultivars.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('breeder', { howMany: 1 }),
        field: 'breeder'
      },
      {
        title: t('culture', { howMany: 1 }),
        field: 'culture.name'
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-cultivar',
        Component: CultivarModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, cultivar) => {
      createDialog({
        id: 'edit-cultivar',
        Component: CultivarModal,
        props: {
          cultivar,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, mod) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'female',
            howMany: 1,
            this: t('cultivar', { howMany: 1 }),
            name: mod.name
          }),
          onConfirm: () => {
            dispatch(CultivarActions.removeCultivar(
              mod,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('cultivar', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'female', howMany: 1 }))
                }))
                mutate()
              },
              ({ response }) => {
                createDialog({
                  id: 'cultivar-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'female',
                      action: toLower(t('remove')),
                      this: toLower(t('cultivar', { howMany: 1 }))
                    })  }.`,
                    message: `${ t(response.data.message) }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const crumbs = useMemo(() => [
    {
      title: t('cultivar', { howMany: 2 }),
      key: 'cultivars-screen',
      url: '/cultivars',
      active: true
    }
  ], [t])

  return (
    <Screen breadcrumbs={ crumbs }>
      <DataTable
        loading={ isValidating }
        data={ cultivars || [] }
        columns={ columns }
        onAddClick={ userActions.write && onAddClick }
        onEditClick={ userActions.edit && onEditClick }
        onDeleteClick={ userActions.delete && onDeleteClick }
      />
    </Screen>
  )
}

export default Cultivars
